/**
 * Shared contracts between the API and the web client.
 *
 * These schemas are the single source of truth: the API validates every
 * request against them, and the web client derives its TypeScript types from
 * the very same definitions. A field renamed here breaks compilation on both
 * sides at once, which is exactly what we want.
 */
import { z } from "zod";

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

/** ISO day, e.g. "2026-08-10". */
export const isoDay = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");

/**
 * Slot time in the notation used throughout the planning, e.g. "09h45".
 * Kept as-is rather than normalised to "09:45" so that exports, the legacy
 * planning file and the UI all speak the same language.
 */
export const slotTime = z
  .string()
  .regex(/^\d{2}h\d{2}$/, "Heure attendue au format 09h45");

/**
 * Staff e-mail. The legacy page enforced the domain in the browser only, which
 * meant anyone could POST anything. It is now checked server-side too.
 */
export const lausanneEmail = z
  .string()
  .trim()
  .toLowerCase()
  .email("Adresse e-mail invalide")
  .refine((v) => v.endsWith("@lausanne.ch"), {
    message: "L'adresse e-mail doit se terminer par @lausanne.ch",
  });

/**
 * Booker e-mail for the reservation form only: unlike staff accounts
 * (`lausanneEmail`), several partner organisations book replacement slots
 * alongside the City itself.
 */
const BOOKER_DOMAINS = ["lausanne.ch", "cpcl.ch", "vaudoisearena.ch", "uplausanne.ch"];
export const bookerEmail = z
  .string()
  .trim()
  .toLowerCase()
  .email("Adresse e-mail invalide")
  .refine((v) => BOOKER_DOMAINS.some((d) => v.endsWith(`@${d}`)), {
    message: `L'adresse e-mail doit se terminer par ${BOOKER_DOMAINS.map((d) => `@${d}`).join(", ")}`,
  });

/**
 * Longueur minimale d'un mot de passe, exportée plutôt que répétée : le
 * schéma, le message d'erreur et le libellé du formulaire la lisent tous ici,
 * de sorte qu'aucun des trois ne peut annoncer une règle que les deux autres
 * n'appliquent pas.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const password = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `${PASSWORD_MIN_LENGTH} caractères minimum`);

export const roleSchema = z.enum(["admin", "intervenant"]);
export type Role = z.infer<typeof roleSchema>;

export const lotSchema = z.enum(["SOI", "Police"]);
export type Lot = z.infer<typeof lotSchema>;

/* -------------------------------------------------------------------------- */
/* Errors                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Stable, machine-readable error codes. The UI switches on these rather than
 * on message text, so wording can change without breaking behaviour.
 */
export const errorCodes = [
  "VALIDATION",
  "NON_AUTHENTIFIE",
  "ACCES_REFUSE",
  "INTROUVABLE",
  "CRENEAU_DEJA_RESERVE",
  "CRENEAU_PASSE",
  "CRENEAU_DEJA_LIVRE",
  "RESERVATION_ANNULEE",
  "CONFLIT_VERSION",
  "STOCK_INCOHERENT",
  "JOUR_DEJA_PLANIFIE",
  "TROP_DE_TENTATIVES",
  "ERREUR_INTERNE",
] as const;
export const errorCodeSchema = z.enum(errorCodes);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const apiErrorSchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  /** Present on CONFLIT_VERSION: the value the caller should rebase onto. */
  details: z.record(z.string(), z.unknown()).optional(),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

/* -------------------------------------------------------------------------- */
/* Authentication                                                              */
/* -------------------------------------------------------------------------- */

export const loginBody = z.object({
  email: z.string().trim().toLowerCase().email("Adresse e-mail invalide"),
  password: z.string().min(1, "Mot de passe requis"),
});
export type LoginBody = z.infer<typeof loginBody>;

export const currentUser = z.object({
  id: z.string().uuid(),
  email: z.string(),
  displayName: z.string(),
  role: roleSchema,
  /** Set for intervenants only: their planning code, "i1".."i4". */
  intervenantCode: z.string().nullable(),
});
export type CurrentUser = z.infer<typeof currentUser>;

/* -------------------------------------------------------------------------- */
/* Catalogue and planning (public)                                             */
/* -------------------------------------------------------------------------- */

export const siteSummary = z.object({
  slug: z.string(),
  name: z.string(),
  lot: lotSchema,
  zone: z.string(),
  firstDay: isoDay,
  lastDay: isoDay,
  totalSlots: z.number().int(),
  freeSlots: z.number().int(),
});
export type SiteSummary = z.infer<typeof siteSummary>;

export const publicSlot = z.object({
  id: z.string().uuid(),
  time: slotTime,
  taken: z.boolean(),
  /** Créneau « grisé » : remplacé par un ajout manuel plus prioritaire. Il
   *  reste affiché pour le contexte, mais n'est plus réservable. */
  superseded: z.boolean(),
});
export type PublicSlot = z.infer<typeof publicSlot>;

export const publicDay = z.object({
  day: isoDay,
  intervenants: z.array(
    z.object({
      code: z.string(),
      displayName: z.string(),
      slots: z.array(publicSlot),
    }),
  ),
});
export type PublicDay = z.infer<typeof publicDay>;

export const siteDetail = siteSummary.extend({
  days: z.array(publicDay),
});
export type SiteDetail = z.infer<typeof siteDetail>;

/* -------------------------------------------------------------------------- */
/* Reservations                                                                */
/* -------------------------------------------------------------------------- */

export const createReservationBody = z.object({
  slotId: z.string().uuid(),
  firstName: z.string().trim().min(1, "Prénom requis").max(80),
  lastName: z.string().trim().min(1, "Nom requis").max(80),
  email: bookerEmail,
  // Saisi à la main, avec ou sans indicatif : aucun format imposé, seulement
  // une longueur bornée. Un motif strict rejetterait des numéros valides.
  contactPhone: z.string().trim().max(40).optional().or(z.literal("")),
  floor: z.string().trim().max(120).optional().or(z.literal("")),
  notes: z.string().trim().max(2000).optional().or(z.literal("")),
});
export type CreateReservationBody = z.infer<typeof createReservationBody>;

/**
 * Avancement du travail derrière une réservation.
 *
 * Distinct de `status`, qui ne dit que si le créneau est encore pris. L'ordre
 * du tableau est celui de l'avancement : l'interface s'en sert pour présenter
 * les états, et il correspond à celui de l'enum PostgreSQL.
 */
export const FULFILLMENT_STATES = ["reservee", "preparation", "livraison", "terminee"] as const;
export const fulfillmentState = z.enum(FULFILLMENT_STATES);
export type FulfillmentState = z.infer<typeof fulfillmentState>;

/** Libellés affichés, au singulier, tels que l'exploitant les lit. */
export const FULFILLMENT_LABELS: Record<FulfillmentState, string> = {
  reservee: "Réservée",
  preparation: "En cours de préparation",
  livraison: "En cours de livraison",
  terminee: "Terminée",
};

/** Passer à cet état compte le poste comme livré dans les indicateurs. */
export const FULFILLMENT_COUNTS_AS_DELIVERED: FulfillmentState = "terminee";

export const setFulfillmentBody = z.object({
  fulfillment: fulfillmentState,
});
export type SetFulfillmentBody = z.infer<typeof setFulfillmentBody>;

export const reservationView = z.object({
  id: z.string().uuid(),
  slotId: z.string().uuid(),
  siteName: z.string(),
  siteSlug: z.string(),
  day: isoDay,
  time: slotTime,
  intervenantName: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  contactPhone: z.string().nullable(),
  floor: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(["active", "cancelled"]),
  fulfillment: fulfillmentState,
  /** Opaque token letting the booker view or cancel without an account. */
  cancelToken: z.string().uuid().optional(),
  calendarState: z.enum(["pending", "synced", "failed", "disabled"]),
  createdAt: z.string(),
});
export type ReservationView = z.infer<typeof reservationView>;

/**
 * Un bâtiment tel que l'écran de gestion le liste, avant dépliage.
 *
 * Même forme que le résumé de la plage publique — période et compteurs — pour
 * que l'exploitant retrouve la lecture qu'il connaît déjà, avec l'avancement
 * à la place des créneaux libres.
 */
export const adminSiteSummary = z.object({
  slug: z.string(),
  name: z.string(),
  zone: z.string(),
  lot: lotSchema,
  firstDay: isoDay,
  lastDay: isoDay,
  /** Réservations actives, annulations exclues. */
  reservations: z.number().int(),
  /** Parmi elles, celles qui comptent déjà comme livrées. */
  done: z.number().int(),
});
export type AdminSiteSummary = z.infer<typeof adminSiteSummary>;

/* -------------------------------------------------------------------------- */
/* Deliveries (intervenant workspace)                                          */
/* -------------------------------------------------------------------------- */

export const deliverySlot = z.object({
  slotId: z.string().uuid(),
  day: isoDay,
  time: slotTime,
  siteSlug: z.string(),
  siteName: z.string(),
  delivered: z.boolean(),
  /** Who booked it — an intervenant only ever sees reserved slots. */
  bookedBy: z.string().nullable(),
});
export type DeliverySlot = z.infer<typeof deliverySlot>;

export const deliveryList = z.object({
  assignedSlots: z.number().int(),
  items: z.array(deliverySlot),
});
export type DeliveryList = z.infer<typeof deliveryList>;

/* -------------------------------------------------------------------------- */
/* Stock                                                                       */
/* -------------------------------------------------------------------------- */

export const stockRow = z.object({
  slug: z.string(),
  name: z.string(),
  lot: lotSchema,
  zone: z.string(),
  plannedSeats: z.number().int(),
  realSeats: z.number().int().nullable(),
  effectiveSeats: z.number().int(),
  delivered: z.number().int(),
  remaining: z.number().int(),
  overDelivered: z.boolean(),
  /** Optimistic-locking token; echo it back on PATCH. */
  version: z.number().int(),
});
export type StockRow = z.infer<typeof stockRow>;

export const patchStockBody = z.object({
  realSeats: z.number().int().min(0).nullable(),
  version: z.number().int(),
});
export type PatchStockBody = z.infer<typeof patchStockBody>;

export const adjustStockBody = z.object({
  /** Positive adds delivered seats, negative removes them. Never zero. */
  delta: z.number().int().refine((v) => v !== 0, "Le delta ne peut pas être nul"),
});
export type AdjustStockBody = z.infer<typeof adjustStockBody>;

/* -------------------------------------------------------------------------- */
/* Dashboard                                                                   */
/* -------------------------------------------------------------------------- */

export const dashboardView = z.object({
  totalSeats: z.number().int(),
  plannedSeats: z.number().int(),
  seatsDelta: z.number().int(),
  totalSlots: z.number().int(),
  reservedSlots: z.number().int(),
  deliveredSeats: z.number().int(),
  deploymentDays: z.number().int(),
  /**
   * Appointments that never reached a calendar at all. Operators must act on
   * these: the booking stands, but nobody's Outlook shows it.
   */
  calendarFailures: z.number().int(),
  /**
   * Appointments the application could not accept on the attendee's behalf.
   * Deliberately counted apart: the meeting *is* in both calendars, the person
   * simply has to accept their invitation as they would any other. Usually an
   * address outside the tenant.
   */
  acceptanceFailures: z.number().int(),
});
export type DashboardView = z.infer<typeof dashboardView>;

/* -------------------------------------------------------------------------- */
/* Intervenant accounts (admin)                                                */
/* -------------------------------------------------------------------------- */

export const intervenantView = z.object({
  userId: z.string().uuid(),
  code: z.string(),
  displayName: z.string(),
  roleLabel: z.string(),
  email: z.string(),
  bookingsUrl: z.string(),
  calendarEmail: z.string(),
  calendarId: z.string(),
  isActive: z.boolean(),
  deliveriesCount: z.number().int(),
});
export type IntervenantView = z.infer<typeof intervenantView>;

export const upsertIntervenantBody = z.object({
  code: z
    .string()
    .trim()
    .regex(/^i\d+$/, "Identifiant attendu au format i1, i2, …"),
  displayName: z.string().trim().min(1).max(120),
  roleLabel: z.string().trim().max(200).default(""),
  email: lausanneEmail,
  bookingsUrl: z.string().trim().url().or(z.literal("")).default(""),
  calendarEmail: z.string().trim().email().or(z.literal("")).default(""),
  /** Empty means the mailbox's default calendar. Set when several
   *  intervenants share one Microsoft account, each with their own
   *  secondary calendar inside it. */
  calendarId: z.string().trim().default(""),
  /** Omit to leave an existing password untouched. */
  password: password.optional(),
});
export type UpsertIntervenantBody = z.infer<typeof upsertIntervenantBody>;

/* -------------------------------------------------------------------------- */
/* Comptes exploitants (admin)                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Un compte exploitant, tel que l'écran de gestion le lit.
 *
 * Le mot de passe n'y figure pas, et pas seulement par omission : le serveur
 * n'en conserve qu'une empreinte, il n'y a donc rien à renvoyer.
 */
export const userView = z.object({
  id: z.string().uuid(),
  email: z.string(),
  displayName: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
  /** Dernière activité observée sur une session, `null` si jamais connecté. */
  lastSeenAt: z.string().nullable(),
});
export type UserView = z.infer<typeof userView>;

export const upsertUserBody = z.object({
  email: lausanneEmail,
  displayName: z.string().trim().min(1, "Nom requis").max(120),
  /** À la création uniquement ; omis en modification, le mot de passe est
   *  laissé tel quel. */
  password: password.optional(),
  /** Réactiver ou désactiver un compte sans le supprimer. */
  isActive: z.boolean().optional(),
});
export type UpsertUserBody = z.infer<typeof upsertUserBody>;

/* -------------------------------------------------------------------------- */
/* Admin: extra slots                                                          */
/* -------------------------------------------------------------------------- */

/**
 * A run of consecutive 45-minute slots starting at a given time. The start
 * must land on a quarter hour (00, 15, 30 or 45) — the planning is built on a
 * quarter-hour grid and a stray `08h07` would never line up with a tour.
 */
export const slotBlock = z
  .object({
    startTime: slotTime,
    slotCount: z.number().int().min(1).max(20).default(1),
  })
  .refine((b) => slotTimeToMinutes(b.startTime) % 15 === 0, {
    message: "L'heure de début doit être un quart d'heure pile (00, 15, 30 ou 45).",
    path: ["startTime"],
  });
export type SlotBlock = z.infer<typeof slotBlock>;

/**
 * The two halves of a working day extra slots can be added to. Kept as two
 * distinct, bounded windows — rather than one free-form list — so a morning
 * block can never be scheduled to start after lunch, or vice versa, and so
 * the two can be checked for overlap against each other and nothing else.
 */
export const MORNING_WINDOW = { start: "08h00", end: "12h00" } as const;
export const AFTERNOON_WINDOW = { start: "12h00", end: "17h30" } as const;

/**
 * One intervenant's contribution to the day: their own building, their own
 * morning/afternoon blocks. Kept per-intervenant rather than one shared
 * building and schedule for everyone, because a real day reconstructed by
 * hand often has each intervenant somewhere different.
 */
export const addSlotsEntry = z
  .object({
    intervenantCode: z.string().regex(/^i\d+$/),
    siteSlug: z.string(),
    morning: slotBlock.optional(),
    afternoon: slotBlock.optional(),
  })
  .refine((e) => e.morning || e.afternoon, {
    message: "Choisissez au moins une plage, matin ou après-midi.",
    path: ["morning"],
  });
export type AddSlotsEntry = z.infer<typeof addSlotsEntry>;

/**
 * Adds capacity for a day. The day must be a weekday; it may already carry
 * planning. When a new slot lands on the same intervenant/day/time as an
 * existing one, the manual slot wins: an existing *free* slot is greyed out
 * (`superseded`), an existing *booked* (or delivered) one rejects the whole
 * request — see admin-slots.routes.ts.
 */
export const addSlotsBody = z.object({
  day: isoDay,
  entries: z.array(addSlotsEntry).min(1),
});
export type AddSlotsBody = z.infer<typeof addSlotsBody>;

export const addSlotsResult = z.object({
  day: isoDay,
  entries: z.array(
    z.object({
      intervenantCode: z.string(),
      siteSlug: z.string(),
      times: z.array(slotTime),
      /** Horaires dont un créneau existant a été grisé pour laisser la place. */
      superseded: z.array(slotTime),
    }),
  ),
});
export type AddSlotsResult = z.infer<typeof addSlotsResult>;

/**
 * Les créneaux déjà en place un jour donné, pour prévenir l'exploitant avant
 * qu'il ne soumette : un créneau `reserved` bloquera l'ajout, un libre sera
 * simplement grisé.
 */
export const adminDaySlot = z.object({
  intervenantCode: z.string(),
  time: slotTime,
  siteSlug: z.string(),
  siteName: z.string(),
  reserved: z.boolean(),
});
export type AdminDaySlot = z.infer<typeof adminDaySlot>;

export const adminDaySlots = z.object({
  day: isoDay,
  slots: z.array(adminDaySlot),
});
export type AdminDaySlots = z.infer<typeof adminDaySlots>;

/* -------------------------------------------------------------------------- */
/* Realtime                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Payloads pushed over SSE. They deliberately carry identifiers rather than
 * full rows: the client refetches, so a stale or reordered event can never
 * write wrong data into the cache.
 */
export const realtimeEvent = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("reservation.created"),
    slotId: z.string().uuid(),
    siteSlug: z.string(),
    day: isoDay,
  }),
  z.object({
    type: z.literal("reservation.cancelled"),
    slotId: z.string().uuid(),
    siteSlug: z.string(),
    day: isoDay,
  }),
  z.object({
    type: z.literal("delivery.toggled"),
    slotId: z.string().uuid(),
    siteSlug: z.string(),
    delivered: z.boolean(),
  }),
  z.object({
    /** L'avancement a changé ; le client refait sa requête pour le lire. */
    type: z.literal("reservation.fulfilled"),
    slotId: z.string().uuid(),
    siteSlug: z.string(),
  }),
  z.object({
    type: z.literal("site.updated"),
    siteSlug: z.string(),
  }),
  z.object({
    type: z.literal("slots.added"),
    siteSlug: z.string(),
    day: isoDay,
  }),
]);
export type RealtimeEvent = z.infer<typeof realtimeEvent>;

/* -------------------------------------------------------------------------- */
/* Formatting helpers shared by both sides                                     */
/* -------------------------------------------------------------------------- */

const MONTHS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
] as const;
const WEEKDAYS = [
  "dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi",
] as const;

/** "2026-08-10" -> "lundi 10 août 2026". Matches the legacy `fj()` output. */
export function formatDayFr(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const weekday = WEEKDAYS[d.getUTCDay()] ?? "";
  const month = MONTHS[d.getUTCMonth()] ?? "";
  return `${weekday} ${d.getUTCDate()} ${month} ${d.getUTCFullYear()}`;
}

/** Monday through Friday only — the planning never runs on a weekend. */
export function isWeekday(iso: string): boolean {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay();
  return day >= 1 && day <= 5;
}

/** A booking must give at least this much notice, to leave time to prepare
 *  the workstation and the technician's round. */
export const BOOKING_NOTICE_HOURS = 24;

/**
 * Europe/Zurich's UTC offset (minutes) at a given instant. Vanilla JS has no
 * timezone-aware date arithmetic, so this reads the offset Intl actually
 * applies for Zurich — CET (UTC+1) or CEST (UTC+2) — rather than assuming one.
 */
function zurichOffsetMinutes(utcGuess: Date): number {
  const part = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Zurich",
    timeZoneName: "shortOffset",
  })
    .formatToParts(utcGuess)
    .find((p) => p.type === "timeZoneName")?.value;
  const m = /GMT([+-])(\d+)(?::(\d+))?/.exec(part ?? "");
  if (!m) return 60;
  const sign = m[1] === "-" ? -1 : 1;
  return sign * (Number(m[2]) * 60 + Number(m[3] ?? 0));
}

/**
 * A slot's start as a real UTC instant. "09h45" on "2026-08-10" is a
 * Europe/Zurich wall-clock time, not a UTC one — the same clock reading is a
 * different instant in summer (CEST) than in winter (CET), and this planning
 * spans both.
 */
export function slotStartUtc(day: string, time: string): Date {
  const guess = new Date(`${day}T00:00:00Z`);
  guess.setUTCMinutes(slotTimeToMinutes(time));
  return new Date(guess.getTime() - zurichOffsetMinutes(guess) * 60_000);
}

/** False once less than `BOOKING_NOTICE_HOURS` remain before the slot starts, or after it has. */
export function isSlotBookable(day: string, time: string): boolean {
  const noticeMs = BOOKING_NOTICE_HOURS * 60 * 60 * 1000;
  return slotStartUtc(day, time).getTime() - Date.now() >= noticeMs;
}

/** Swiss-French thousands separator, as used across the back office. */
export function formatNumberFr(n: number): string {
  return Number(n).toLocaleString("de-CH");
}

/** "09h45" -> minutes since midnight. Used for ordering and for Graph. */
export function slotTimeToMinutes(time: string): number {
  const m = /^(\d{2})h(\d{2})$/.exec(time);
  if (!m) throw new Error(`Heure de créneau invalide : ${time}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Every intervention lasts 45 minutes — a rule the planning engine relies on. */
export const SLOT_DURATION_MINUTES = 45;

/** "09h45" + 45min -> "10h30". */
export function slotEndTime(time: string): string {
  const total = slotTimeToMinutes(time) + SLOT_DURATION_MINUTES;
  const hh = String(Math.floor(total / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}h${mm}`;
}

/**
 * `count` consecutive 45-minute slots starting at `start`. Shared so the
 * admin UI's preview and the server's actual creation can never disagree
 * about what a given start time and count produce.
 */
export function slotTimesFrom(start: string, count: number): string[] {
  const times = [start];
  for (let i = 1; i < count; i++) times.push(slotEndTime(times[i - 1]!));
  return times;
}
